import React from "react";
import { SOR_STATUS, FLOW_STATUS, PAYMENT_STATUS } from "utils/constants/onboarding";
import { STATUS_BEGIN, STATUS_FAIL } from "utils/constants/others";
import styles from "./Form.module.scss";

function StatusTimeline({ step2Done = false, step3Done = false, needsChanges = false }) {
  return (
    <div className={styles.statusCard}>
      <p className={styles.statusCardTitle}>Onboarding progress</p>
      <div className={styles.statusSteps}>
        <div className={styles.statusStep}>
          <div className={`${styles.statusDot} ${styles.statusDotDone}`} />
          <div className={`${styles.statusStepLine} ${styles.statusStepLineDone}`} />
          <div className={styles.statusStepContent}>
            <span className={styles.statusStepLabel}>Application submitted</span>
            <span className={styles.statusStepSub}>We’ve received your details</span>
          </div>
        </div>
        <div className={styles.statusStep}>
          <div className={`${styles.statusDot} ${step2Done ? styles.statusDotDone : styles.statusDotCurrent}`} />
          <div className={`${styles.statusStepLine} ${step2Done ? styles.statusStepLineDone : ""}`} />
          <div className={styles.statusStepContent}>
            <span className={styles.statusStepLabel}>
              {needsChanges ? "Changes requested" : step2Done ? "Review completed" : "Under review"}
            </span>
            <span className={styles.statusStepSub}>
              {needsChanges ? "Please update the requested details" : step2Done ? "Your details have been checked" : "We’re checking your details"}
            </span>
          </div>
        </div>
        <div className={styles.statusStep}>
          <div className={`${styles.statusDot} ${step3Done ? styles.statusDotDone : styles.statusDotPending}`} />
          <div className={styles.statusStepContent}>
            <span className={styles.statusStepLabel}>{step3Done ? "Application approved" : "Approval"}</span>
            <span className={styles.statusStepSub}>
              {step3Done ? "You’re ready to get started" : needsChanges ? "Submit your updates to continue" : "We’ll notify you once it’s done"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RefundInfo({ fee, reviewComplete = false, needsResubmission = false }) {
  if (!fee) return null;

  const formattedFee = `₹${fee.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  return (
    <div className={`${styles.refundableBadge} ${styles.statusRefundCard}`}>
      <span className={styles.refundableTick}>✓</span>
      <div>
        <div className={styles.refundableTitle}>
          {reviewComplete ? "REFUND UPDATE" : "100% REFUNDABLE FEE"}
        </div>
        <div className={styles.refundableDesc}>
          {reviewComplete
            ? `Your ${formattedFee} onboarding fee will be returned to your original payment method. It may take some time to reflect.`
            : `Your ${formattedFee} onboarding fee will be returned after the application review is completed.`}
          {needsResubmission && " You’ll pay the refundable fee again when you resubmit."}
        </div>
      </div>
    </div>
  );
}

export default function OnboardingStatusScreen({
  sorStatus,
  flowStatus,
  paymentStatus,
  rejectionReason,
  sellerPhone,
  sellerEmail,
  hasFee,
  onboardingFee,
  retryPayment,
  setRetryPayment,
  selfOnboardStatus,
  pollPaymentExhausted,
  onViewForm,
  onFetchFlow,
  onRetryOnboard,
  onEditRejected,
  onLoginSellerHub,
}) {
  if (sorStatus === SOR_STATUS.COMPLETED) {
    return (
      <div className={styles.successScreen}>
        <div className={`${styles.successIconWrap} ${styles.iconWrapGreen}`}>
          <img src="/logos/hyperpure-logo.svg" alt="Hyperpure" className={styles.successIconImg} />
        </div>
        <h1 className={styles.successTitle}>You’re all set</h1>
        <p className={styles.successText}>Your application has been approved. You can now log in and get started.</p>
        <RefundInfo fee={onboardingFee} reviewComplete />
        <StatusTimeline step2Done step3Done />
        <div className={styles.statusActions}>
          <button type="button" className={styles.statusBtn} onClick={onLoginSellerHub}>Log in</button>
          <button type="button" className={styles.ghostBtn} onClick={onViewForm}>View application</button>
        </div>
      </div>
    );
  }

  if (sorStatus === SOR_STATUS.IN_PROGRESS) {
    return (
      <div className={styles.successScreen}>
        <div className={`${styles.successIconWrap} ${styles.iconWrapBlue}`}>
          <img src="/logos/info_logo.svg" alt="Under review" className={styles.successIconImg} />
        </div>
        <h1 className={styles.successTitle}>We’re reviewing your application</h1>
        <p className={styles.successText}>We’ll let you know when the review is complete or if we need anything else.</p>
        <RefundInfo fee={onboardingFee} />
        <StatusTimeline />
        <div className={styles.statusActions}>
          <button type="button" className={styles.statusBtn} onClick={onFetchFlow}>Refresh status</button>
          <button type="button" className={styles.ghostBtn} onClick={onViewForm}>View application</button>
        </div>
      </div>
    );
  }

  if (sorStatus === SOR_STATUS.INITIATED && rejectionReason && !retryPayment) {
    const canEdit = (
      flowStatus === FLOW_STATUS.CREATED ||
      flowStatus === FLOW_STATUS.ASSIGNED ||
      flowStatus === FLOW_STATUS.INITIATED
    );
    const canRetrySubmission = (
      flowStatus === FLOW_STATUS.COMPLETED &&
      selfOnboardStatus === STATUS_FAIL
    );

    return (
      <div className={styles.successScreen}>
        <div className={`${styles.successIconWrap} ${styles.iconWrapRed}`}>
          <img src="/logos/bang.svg" alt="Application needs changes" className={styles.successIconImg} />
        </div>
        <h1 className={styles.successTitle}>Your application needs an update</h1>
        <p className={styles.successText}>Please update the details mentioned below and submit your application again.</p>
        <div className={styles.statusFeedbackCard}>
          <span className={styles.statusFeedbackLabel}>What needs to be updated</span>
          <p>{rejectionReason}</p>
        </div>
        <RefundInfo fee={onboardingFee} reviewComplete needsResubmission />
        <StatusTimeline step2Done needsChanges />
        <div className={styles.statusActions}>
          <button
            type="button"
            className={styles.statusBtn}
            onClick={canEdit ? onEditRejected : canRetrySubmission ? onRetryOnboard : onViewForm}
          >
            {canEdit ? "Edit application" : canRetrySubmission ? "Try submitting again" : "Review application"}
          </button>
          <button type="button" className={styles.ghostBtn} onClick={onFetchFlow}>Refresh status</button>
        </div>
      </div>
    );
  }

  if (flowStatus !== FLOW_STATUS.COMPLETED) return null;

  if (paymentStatus === PAYMENT_STATUS.CANCELLED && !retryPayment) {
    return (
      <div className={styles.successScreen}>
        <div className={`${styles.successIconWrap} ${styles.iconWrapRed}`}>
          <img src="/logos/bang.svg" alt="Payment failed" className={styles.successIconImg} />
        </div>
        <h1 className={styles.successTitle}>Payment failed</h1>
        <p className={styles.successText}>Your application is saved. Please try the payment again.</p>
        <div className={styles.statusActions}>
          <button type="button" className={styles.statusBtn} onClick={() => setRetryPayment(true)}>Try payment again</button>
          <button type="button" className={styles.ghostBtn} onClick={onViewForm}>View application</button>
        </div>
      </div>
    );
  }

  if (paymentStatus === PAYMENT_STATUS.INITIATED) {
    return (
      <div className={styles.successScreen}>
        <div className={`${styles.successIconWrap} ${styles.iconWrapBlue}`}>
          <div className={styles.processingSpinner} />
        </div>
        <h1 className={styles.successTitle}>Processing your payment</h1>
        <p className={styles.successText}>This may take a few moments. Please keep this page open.</p>
        {pollPaymentExhausted && <p className={styles.takingLongerText}>This is taking longer than usual.</p>}
        <button type="button" className={styles.ghostBtn} onClick={onFetchFlow}>Refresh status</button>
      </div>
    );
  }

  const isPaymentDone = !hasFee || paymentStatus === PAYMENT_STATUS.COMPLETED;
  if (isPaymentDone && !retryPayment) {
    if (selfOnboardStatus === STATUS_BEGIN) {
      return (
        <div className={styles.successScreen}>
          <div className={`${styles.successIconWrap} ${styles.iconWrapBlue}`}>
            <div className={styles.processingSpinner} />
          </div>
          <h1 className={styles.successTitle}>Submitting your application</h1>
          <p className={styles.successText}>Please wait while we finish your submission.</p>
        </div>
      );
    }

    const retryFailed = selfOnboardStatus === STATUS_FAIL && onRetryOnboard;
    return (
      <div className={styles.successScreen}>
        <div className={`${styles.successIconWrap} ${retryFailed ? styles.iconWrapRed : styles.iconWrapGreen}`}>
          <img
            src={retryFailed ? "/logos/bang.svg" : "/logos/tick.svg"}
            alt={retryFailed ? "Submission failed" : "Application submitted"}
            className={styles.successIconImg}
          />
        </div>
        <h1 className={styles.successTitle}>
          {retryFailed ? "Submission couldn’t be completed" : "Application submitted"}
        </h1>
        <p className={styles.successText}>
          {retryFailed
            ? "Your application is saved. Please try submitting it again."
            : "We’ve received your application and will let you know when the review is complete."}
        </p>
        {!retryFailed && (sellerPhone || sellerEmail) && (
          <p className={styles.commsConfirmText}>
            Confirmation sent to {sellerPhone && <strong>{sellerPhone}</strong>}
            {sellerPhone && sellerEmail && " and "}
            {sellerEmail && <strong>{sellerEmail}</strong>}.
          </p>
        )}
        <RefundInfo fee={onboardingFee} />
        <StatusTimeline />
        <div className={styles.statusActions}>
          <button
            type="button"
            className={styles.statusBtn}
            onClick={retryFailed ? onRetryOnboard : onFetchFlow}
          >
            {retryFailed ? "Try again" : "Refresh status"}
          </button>
          <button type="button" className={styles.ghostBtn} onClick={onViewForm}>View application</button>
        </div>
      </div>
    );
  }

  return null;
}
